import React, { useEffect, useState, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { Box, Button, Typography, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../../api/api';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import { updateOperations } from '../../redux/actionCreators/operations';
import { updateCurrentLineItem } from '../../redux/actionCreators/station';
import { markComplete } from '../../redux/actionCreators/printBarcode';
import {
  getProcessValidationDetailsForVcNumber,
  getProcessValidationDetailsForSerialNumber,
  privilegedSkip,
  getFeatures,
} from '../../redux/actionCreators/processValidation';

import {
  OPERATIONS,
  REDIRECT_URLS,
  ERROR_MESSAGE,
  DARK_MODE,
  DEFAULT_COLUMNS,
  MACHINE_DEFAULT_COLUMNS,
  isPuneJlr,
} from '../../constants';
import { getNextOperation, hasOperationPermission, getNavigableOperations } from '../../utils/operationFlow';
import { useRedirectToNextScreen, useResolutionScale } from '../../hooks';
import { StyledTable, JlrButton, ConfirmMarkCompleteDialog } from '../../components';
import { useTorqueColumns } from '../../components/TorqueTable/useTorqueColumns';
import { AddDefectButton } from '../../components/TableActionBar/TableActionBar';
import DefectModal from '../../components/ReportModal/DefectModal';
import styles from './Machine.module.scss';
import ReLogin from '../../components/ReLogin/ReLogin';
import ReasonAndRemarkModal from '../../components/ReportModal/ReasonAndRemarkModal';
import { encryptStationPassword } from '../../utils/EncryptionUtils';
import { Checkbox } from '@mui/material';
import { toast } from 'react-toastify';

const Machine = ({
  currentLineItem,
  updateOperations,
  updateCurrentLineItem,
  markComplete,
  allowedOperations,
  getProcessValidationDetailsForVcNumber,
  getProcessValidationDetailsForSerialNumber,
  privilegedSkip,
  getFeatures,
  themeMode,
  user,
  receivePart,
  partValidation,
}) => {
  const navigate = useNavigate();
  const redirect = useRedirectToNextScreen();
  const { showMessageOverlay } = useMessageContextManager();
  const [machineData, setMachineData] = useState([]);

  const [selectedMachineRowId, setSelectedMachineRowId] = useState(null);
  const [vcTorqueRows, setVcTorqueRows] = useState([]);
  const [torqueTableRows, setTorqueTableRows] = useState([]);
  const [features, setFeatures] = useState([]);
  const [isFeaturesLoaded, setIsFeaturesLoaded] = useState(false);

  const isDisplayOverallParamsFalse = features.find((f) => f.name === 'DISPLAY_OVERALL_PARAMS')?.enabled === false;
  const torqueTableHeader = useTorqueColumns(isDisplayOverallParamsFalse);

  const [selectedTorqueRows, setSelectedTorqueRows] = useState([]);

  const [selectedGlobalRow, setSelectedGlobalRow] = useState(null);
  const [skippedTorqueRow, setSkippedTorqueRow] = useState(null);
  const [selectedPvRows, setSelectedPvRows] = useState([]);
  const [isConfirmMarkCompleteOpen, setIsConfirmMarkCompleteOpen] = useState(false);
  const { scaleValue } = useResolutionScale();

  const secondApiIntervalRef = useRef(null);
  const stopSecondApiRef = useRef(false);
  const firstApiSeqNumRef = useRef(null);
  const baseRowsFromFirstApiRef = useRef([]);

  // ─────────────────────────────────────────────────────────────────────────────
  // FIX: Store the LOCKED serial number in a ref so the interval always uses
  //      the same VIN that triggered the effect — never a stale/old value.
  // ─────────────────────────────────────────────────────────────────────────────
  const lockedSerialNumberRef = useRef(null);

  const [showReLoginModal, setShowReLoginModal] = useState(false);
  const [showSkipRemarksModal, setShowSkipRemarksModal] = useState(false);

  const [groupLeaderCredentials, setGroupLeaderCredentials] = useState({
    username: '',
    password: '',
  });
  const [skipReasonData, setSkipReasonData] = useState({ reason: '', remarks: '' });
  const [secondApiResponse, setSecondApiResponse] = useState(null);
  const [isDefectModalOpen, setIsDefectModalOpen] = useState(false);

  const hasMachineOperation = hasOperationPermission(OPERATIONS.MACHINE, allowedOperations);
  const navigableOps = getNavigableOperations(allowedOperations);
  const isMachineLastOp = navigableOps[navigableOps.length - 1] === OPERATIONS.MACHINE;

  // ─────────────────────────────────────────────────────────────────────────────
  // FIX: Derive a SINGLE resolved serial number outside the effect.
  //      Priority: partValidation > receivePart > currentLineItem
  //      This is the ONLY source of truth — never mix three sources inside
  //      the effect body, as they update at different Redux dispatch times.
  // ─────────────────────────────────────────────────────────────────────────────
  const resolvedSerialNumber =
    partValidation?.serialNumber ||
    receivePart?.serialNumber ||
    currentLineItem?.serialNumber ||
    null;

  // ─────────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────────

  const normalize = (v = '') => {
    if (!v) return '';
    return v
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/\s*__\s*/g, '__')
      .replace(/\s*_\s*/g, '_');
  };

  const isSuccessStatus = (status) => {
    if (!status) return false;
    const s = status.toString().toUpperCase();
    return s === 'OK' || s === 'SUCCESS' || s === 'VALIDATED' || s === 'PASS' || s === 'APPLIED';
  };

  const buildStatusMap = (serialResponse) => {
    const map = {};
    const machineDataArr = serialResponse?.machineData || [];
    machineDataArr.forEach((machine) => {
      const machineId = machine.machineId;
      machine.processParameters?.forEach((param) => {
        const key = `${machineId}_${normalize(param.key)}`;
        if (!map[key]) map[key] = {};
        if (param.batchNumber !== undefined && param.batchNumber !== null) {
          map[key][param.batchNumber] = {
            status: param.status,
            isSkipped: param.isSkipped,
          };
        }
      });
    });
    return map;
  };

  const extractRowsFromSecondApi = (serialResponse = {}) => {
    const machineDataArr = serialResponse?.machineData || [];
    const validMachines = machineDataArr.filter((machine) => machine.machineId !== 50001);
    return validMachines.flatMap((machine) =>
      (machine.processParameters || []).map((param, index) => ({
        ...param,
        parameterKey: param.key || param.parameterKey,
        quantity: param.quantity ?? 1,
        machineId: machine.machineId,
        id: `${machine.machineId}-${param.key || param.parameterKey}-${index}`,
        status: 'Pending',
        paramStatus: param.status,
      }))
    );
  };

  const mergeTorqueStatus = (vcRows = [], serialResponse = {}) => {
    const statusMap = buildStatusMap(serialResponse);
    return vcRows.map((row) => {
      const mapKey = `${row.machineId}_${normalize(row.parameterKey)}`;
      const batchStatusMap = statusMap[mapKey] || {};

      const getBatchData = (i) => {
        let batchNum = i + 1;
        let data = batchStatusMap[batchNum];
        if (data === undefined && i === 0) data = batchStatusMap[0];
        if (data === undefined && i === 0) {
          const keys = Object.keys(batchStatusMap);
          if (keys.length === 1) data = batchStatusMap[keys[0]];
        }
        // Pune JLR: row.batchNumber (from API spread) is the definitive batch key
        if (data === undefined && isPuneJlr() && row.batchNumber != null) {
          data = batchStatusMap[row.batchNumber];
        }
        return data || null;
      };

      const getActualBatchNum = (i) => {
        if (batchStatusMap[i + 1] !== undefined) return i + 1;
        if (i === 0 && batchStatusMap[0] !== undefined) return 0;
        if (i === 0) {
          const keys = Object.keys(batchStatusMap).map(Number);
          if (keys.length === 1) return keys[0];
        }
        // Pune JLR: use the row's own batchNumber from the API
        if (isPuneJlr() && row.batchNumber != null && batchStatusMap[row.batchNumber] !== undefined) {
          return row.batchNumber;
        }
        return i + 1;
      };

      const executionStatuses = Array.from({ length: row.quantity ?? 1 }, (_, i) => {
        const data = getBatchData(i);
        if (!data) return null;
        if (data.isSkipped === true) return 'skipped';
        if (isSuccessStatus(data.status)) return true;
        if (data.status && data.status.toString().toUpperCase() === 'NOK') return false;
        return null;
      });

      const batchRawStatuses = Array.from({ length: row.quantity ?? 1 }, (_, i) => {
        const data = getBatchData(i);
        if (!data) {
          if (isPuneJlr()) {
            const fallback = row.paramStatus || row.status;
            return (fallback && fallback.toUpperCase() !== 'PENDING') ? fallback : 'PENDING';
          }
          return 'PENDING';
        }
        if (data.isSkipped === true) return 'SKIPPED';
        const statusValue = data.status;
        if (isPuneJlr() && (!statusValue || statusValue.toUpperCase() === 'PENDING')) {
          const fallback = row.paramStatus || row.status;
          if (fallback && fallback.toUpperCase() !== 'PENDING') {
            return fallback;
          }
        }
        return statusValue || 'PENDING';
      });

      const batchActualNumbers = isPuneJlr()
        ? Array.from({ length: row.quantity ?? 1 }, (_, i) => getActualBatchNum(i))
        : Array.from({ length: row.quantity ?? 1 }, (_, i) => i + 1);

      let status = 'Pending';
      if (executionStatuses.every((v) => v === true || v === 'skipped')) {
        status = executionStatuses.every((v) => v === 'skipped') ? 'SKIPPED' : 'OK';
      } else if (executionStatuses.some((v) => v === false)) {
        status = 'NOK';
      } else if (executionStatuses.some((v) => v === true || v === 'skipped')) {
        status = 'Partial';
      }

      return { ...row, status, executionStatuses, batchRawStatuses, batchActualNumbers };
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // FIX: callSecondApi now reads from lockedSerialNumberRef instead of any
  //      closure variable. This guarantees the interval always calls the API
  //      for the VIN that was active when the effect ran — not a stale VIN.
  // ─────────────────────────────────────────────────────────────────────────────
  const callSecondApi = useCallback(
    (serialNumber) => {
      // Guard: reject the call if the serial number doesn't match the locked VIN
      // This prevents a race condition where a previous interval tick fires
      // just after a new VIN has been scanned.
      if (!serialNumber || serialNumber !== lockedSerialNumberRef.current) {
        return;
      }

      getProcessValidationDetailsForSerialNumber(
        serialNumber,
        (data) => {
          setSecondApiResponse(data);
          const baseRows = baseRowsFromFirstApiRef.current;

          if (baseRows.length === 0) {
            const fallbackRows = extractRowsFromSecondApi(data);
            if (fallbackRows.length > 0) {
              baseRowsFromFirstApiRef.current = fallbackRows;
              const updatedRows = mergeTorqueStatus(fallbackRows, data);
              setTorqueTableRows(updatedRows);
            }
            return;
          }

          const updatedRows = mergeTorqueStatus(baseRows, data);
          setTorqueTableRows(updatedRows);
        },
        (error) => {
          console.error('Second API error:', error);
        }
      );
    },
    [getProcessValidationDetailsForSerialNumber]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Main effect — depends ONLY on the single resolved serial number.
  //
  // BEFORE (broken): three separate deps (partValidation?.serialNumber,
  //   receivePart?.serialNumber, currentLineItem?.serialNumber) caused the
  //   effect to re-run multiple times as each Redux slice updated separately,
  //   each time potentially reading a mix of old and new VINs.
  //
  // AFTER (fixed): one derived dep (resolvedSerialNumber) means the effect
  //   fires exactly once per VIN change, always with a consistent value.
  // ─────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    getFeatures(
      (data) => {
        setFeatures(data);
        setIsFeaturesLoaded(true);
      },
      (error) => {
        console.error('Failed to fetch features:', error);
        setIsFeaturesLoaded(true);
      }
    );
  }, [getFeatures]);

  useEffect(() => {
    if (!isFeaturesLoaded) return;

    stopSecondApiRef.current = false;

    if (!hasMachineOperation) return;
    if (!resolvedSerialNumber) return;

    // ── Lock the serial number for this effect run ──
    // Everything downstream (interval, callbacks) reads from this ref,
    // so there is no way for a later Redux update to sneak in a different VIN.
    lockedSerialNumberRef.current = resolvedSerialNumber;

    // Reset state for new VIN
    baseRowsFromFirstApiRef.current = [];
    firstApiSeqNumRef.current = null;
    setVcTorqueRows([]);
    setTorqueTableRows([]);
    setSecondApiResponse(null);
    setSelectedTorqueRows([]);
    setSkippedTorqueRow(null);
    setSelectedGlobalRow(null);

    // Clear any previous interval before starting a new one
    if (secondApiIntervalRef.current) {
      clearInterval(secondApiIntervalRef.current);
      secondApiIntervalRef.current = null;
    }

    const firstSuccessCallback = (data) => {
      if (!Array.isArray(data)) {
        baseRowsFromFirstApiRef.current = [];
        setVcTorqueRows([]);
        setTorqueTableRows([]);
        setSecondApiResponse(null);
        return;
      }

      const validMachines = data.filter((machine) => machine.machineId !== 50001);

      if (data.length > 0) {
        const itemWithSeq = data.find((item) => item.sequenceNumber || item.asn) || data[0];
        firstApiSeqNumRef.current = itemWithSeq?.sequenceNumber || itemWithSeq?.asn || null;
      }

      const baseRows = validMachines.flatMap((machine) => {
        const params = machine.parameters || machine.processParameters || [];
        return params.map((param, index) => ({
          ...param,
          parameterKey: param.parameterKey || param.key,
          quantity: param.quantity ?? 1,
          machineId: machine.machineId,
          id: `${machine.machineId}-${param.parameterKey || param.key}-${index}`,
          status: 'Pending',
          paramStatus: param.status,
          executionStatuses: Array.from({ length: param.quantity ?? 1 }, () => null),
        }));
      });

      baseRowsFromFirstApiRef.current = baseRows;
      setVcTorqueRows(baseRows);
      setTorqueTableRows(baseRows);

      // Initial second API call — pass the locked VIN explicitly
      callSecondApi(lockedSerialNumberRef.current);

      // Start polling interval — always pass lockedSerialNumberRef.current
      // so it never captures a stale closure VIN
      secondApiIntervalRef.current = setInterval(() => {
        if (stopSecondApiRef.current) return;
        callSecondApi(lockedSerialNumberRef.current);
      }, 5000);
    };

    const firstErrorCallback = () => {
      baseRowsFromFirstApiRef.current = [];
      setVcTorqueRows([]);
      setTorqueTableRows([]);
      setSecondApiResponse(null);
    };

    const displayOverallParamsFeature = features.find((f) => f.name === 'DISPLAY_OVERALL_PARAMS');
    const shouldSkipFirstApi = displayOverallParamsFeature && displayOverallParamsFeature.enabled === false;

    if (shouldSkipFirstApi) {
      callSecondApi(lockedSerialNumberRef.current);
      secondApiIntervalRef.current = setInterval(() => {
        if (stopSecondApiRef.current) return;
        callSecondApi(lockedSerialNumberRef.current);
      }, 5000);
    } else {
      getProcessValidationDetailsForVcNumber(
        resolvedSerialNumber,
        firstSuccessCallback,
        firstErrorCallback
      );
    }

    return () => {
      // Cleanup: stop polling when VIN changes or component unmounts
      stopSecondApiRef.current = true;
      if (secondApiIntervalRef.current) {
        clearInterval(secondApiIntervalRef.current);
        secondApiIntervalRef.current = null;
      }
    };
  }, [
    hasMachineOperation,
    resolvedSerialNumber,
    isFeaturesLoaded,
    features,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────────────────────

  const handleTorqueSelectionChange = (selectedIds = []) => {
    setSelectedPvRows([]);
    setSelectedGlobalRow(null);
    setSelectedTorqueRows(selectedIds || []);

    if (selectedIds.length > 0) {
      const firstSelectedRow = torqueTableRows.find((row) => selectedIds.includes(row.id));
      setSkippedTorqueRow(firstSelectedRow || null);
      setSelectedGlobalRow(firstSelectedRow || null);
    } else {
      setSkippedTorqueRow(null);
      setSelectedGlobalRow(null);
    }
  };

  const handleMarkComplete = () => {
    setIsConfirmMarkCompleteOpen(true);
  };

  const handleNext = () => {
    stopSecondApiRef.current = true;
    if (secondApiIntervalRef.current) {
      clearInterval(secondApiIntervalRef.current);
      secondApiIntervalRef.current = null;
    }
    const completedOperations = currentLineItem?.completedOperations || [];
    const updatedCompletedOperations = [...new Set([...completedOperations, OPERATIONS.MACHINE])];
    updateCurrentLineItem({ ...currentLineItem, completedOperations: updatedCompletedOperations });
    const nextOperation = getNextOperation(updatedCompletedOperations, allowedOperations);
    updateOperations({ currentOperation: nextOperation });
    const nextPath = REDIRECT_URLS[nextOperation];
    redirect(() => navigate(nextPath));
  };

  const executeMarkComplete = () => {
		setIsConfirmMarkCompleteOpen(false);

		stopSecondApiRef.current = true;
		if (secondApiIntervalRef.current) {
			clearInterval(secondApiIntervalRef.current);
			secondApiIntervalRef.current = null;
		}

		const sequenceNumber =
			firstApiSeqNumRef.current ||
			currentLineItem?.sequenceNumber ||
			partValidation?.sequenceNumber ||
			currentLineItem?.asn ||
			partValidation?.asn;

		markComplete(
			() => {
				callSecondApi(lockedSerialNumberRef.current);

				const completedOperations = currentLineItem?.completedOperations || [];
				const updatedCompletedOperations = [
					...completedOperations,
					OPERATIONS.MACHINE,
				];

				updateCurrentLineItem({
					...currentLineItem,
					completedOperations: updatedCompletedOperations,
				});

				const nextOperation = getNextOperation(
					updatedCompletedOperations,
					allowedOperations,
				);

				updateOperations({ currentOperation: nextOperation });

				const nextPath =
					REDIRECT_URLS[nextOperation] || '/assembly/receive-part';
				navigate(nextPath);
			},
			(error) => {
				showMessageOverlay({
					contextText: ERROR_MESSAGE.MARK_COMPLETE_ERROR_MESSAGE,
					info: error?.response?.data?.message || 'Failed to mark complete',
					error: true,
				});
			},
			sequenceNumber,
		);
	};

  const handleSkip = () => {
    setShowSkipRemarksModal(true);
  };

  const handleSkipMachineOperation = (reason, remarks) => {
    setSkipReasonData({ reason, remarks });
    setShowSkipRemarksModal(false);
    setShowReLoginModal(true);
  };

  const handleReLoginSuccess = async (e, username, password) => {
    e.preventDefault();
    try {
      const privilegedRequest = {
        username,
        password,
        action: 'GROUP_LEADER',
        remark: skipReasonData.remarks,
      };

      const currentSelection = selectedTorqueRows || [];
      const rowsToProcess = torqueTableRows.filter((row) => currentSelection.includes(row.id));

      if (rowsToProcess.length === 0) {
        setShowReLoginModal(false);
        toast.warning('Please select a row to skip');
        return;
      }

      const machineGroups = rowsToProcess.reduce((acc, row) => {
        if (!row.machineId) return acc;
        if (!acc[row.machineId]) acc[row.machineId] = [];

        const qty = row.quantity || 1;
        const executionStatuses = row.executionStatuses || Array.from({ length: qty }, () => null);

        const batchRawStatuses = row.batchRawStatuses || Array.from({ length: qty }, () => {
          if (isPuneJlr()) {
            const fallback = row.paramStatus || row.status;
            return (fallback && fallback.toUpperCase() !== 'PENDING') ? fallback : 'PENDING';
          }
          return 'PENDING';
        });

        for (let i = 0; i < qty; i++) {
          const isOk = executionStatuses[i] === true;
          if (!isOk) {
            let finalStatus = batchRawStatuses[i];
            if (isPuneJlr()) {
              const currentStatus = batchRawStatuses[i];
              if (!currentStatus || currentStatus.toUpperCase() === 'PENDING') {
                const fallback = row.paramStatus || row.status;
                if (fallback && fallback.toUpperCase() !== 'PENDING') {
                  finalStatus = fallback;
                } else {
                  finalStatus = 'PENDING';
                }
              } else {
                finalStatus = currentStatus;
              }
            }
            acc[row.machineId].push({
              parameterName: row.parameterKey || row.parameterName || '',
              parameterDescription: row.parameterDescription || row.parameterName || row.parameterKey || '',
              status: finalStatus,
              unit: row.unit || '',
              value: row.value || '',
              batchNumber: isPuneJlr() ? (row.batchNumber || (i + 1)) : (i + 1),
              reason: skipReasonData.reason,
              remark: skipReasonData.remarks,
            });
          }
        }

        return acc;
      }, {});

      const parametersSkipRequest = Object.keys(machineGroups)
        .map((machineId) => ({
          machineId: parseInt(machineId),
          processesToSkip: machineGroups[machineId],
        }))
        .filter((group) => group.processesToSkip.length > 0);

      if (parametersSkipRequest.length === 0) {
        setShowReLoginModal(false);
        toast.info('All batches for this parameter are already OK');
        return;
      }

      const payload = {
        partSkipRequest: null,
        parametersSkipRequest,
        privilegedRequest,
      };

      const skipSequenceNumber =
        firstApiSeqNumRef.current ||
        currentLineItem?.sequenceNumber ||
        partValidation?.sequenceNumber ||
        currentLineItem?.asn ||
        partValidation?.asn;

      privilegedSkip(
        skipSequenceNumber,
        payload,
        () => {
          setShowReLoginModal(false);
          // Always use the locked VIN for refresh — never stale Redux state
          callSecondApi(lockedSerialNumberRef.current);
          setSelectedTorqueRows([]);
          setSkippedTorqueRow(null);
          setSelectedGlobalRow(null);
          toast.success('Parameter(s) skipped successfully');
        },
        (error) => {
          setShowReLoginModal(false);
          showMessageOverlay({
            contextText: 'Skip Failed',
            info: error?.response?.data?.message || 'Failed to skip machine operation',
            error: true,
          });
        }
      );
    } catch (err) {
      setShowReLoginModal(false);
      showMessageOverlay({
        contextText: 'Skip Error',
        info: err.message,
        error: true,
      });
    }
  };

  const isMarkCompleteDisabled = () => {
    if (!torqueTableRows || torqueTableRows.length === 0) return false;
    if (!secondApiResponse || !secondApiResponse.machineData) return true;

    const statusMap = buildStatusMap(secondApiResponse);

    for (const row of torqueTableRows) {
      const mapKey = `${row.machineId}_${normalize(row.parameterKey)}`;
      const batchStatusMap = statusMap[mapKey] || {};
      const quantity = row.quantity || 1;

      // Pune JLR uses non-sequential batch numbers mapped in batchActualNumbers
      const batchNums = (isPuneJlr() && Array.isArray(row.batchActualNumbers))
        ? row.batchActualNumbers
        : Array.from({ length: quantity }, (_, i) => i + 1);

      for (let i = 0; i < batchNums.length; i++) {
        const batchNum = batchNums[i];
        let batchData = batchStatusMap[batchNum];
        if (batchData === undefined && i === 0) {
          batchData = batchStatusMap[0];
        }
        if (batchData === undefined && i === 0 && !isPuneJlr()) {
          const keys = Object.keys(batchStatusMap);
          if (keys.length === 1) batchData = batchStatusMap[keys[0]];
        }
        if (!batchData) return true;

        const status = (batchData.status || '').toUpperCase().trim();
        if (status === 'OK') continue;
        if ((status === 'NOK' || status === 'PENDING') && batchData.isSkipped !== true) return true;
      }
    }

    return false;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Derived display values
  // ─────────────────────────────────────────────────────────────────────────────

  const serialNumber = partValidation?.serialNumber || receivePart?.serialNumber || currentLineItem?.serialNumber;
  const partNumber = partValidation?.parentPartNumber || currentLineItem?.partNumber;
  const description = partValidation?.parentPartDescription || currentLineItem?.description;
  const productionDt = currentLineItem?.productionDt;
  const lotNumber = currentLineItem?.lotNumber;
  const model = currentLineItem?.model;
  const modelYear = currentLineItem?.modelYear;

  const currentLineItemData = serialNumber
    ? [
        {
          serialNumber,
          partNumber,
          description,
          productionDt,
          lotNumber,
          model,
          modelYear,
          id: currentLineItem?.sequenceNumber || 'current',
        },
      ]
    : [];


  return (
    <Box
      className={`${styles.backgroundBase} ${
        themeMode === DARK_MODE ? styles.backgroundBaseDark : styles.backgroundBaseLight
      }`}
    >
      <Box className={styles.contentContainer}>
        <Paper className={styles.tableContainer}>
          <Typography variant="h6" sx={{ p: 2, color: themeMode === DARK_MODE ? '#fff' : '#000' }}>
            Current KSPEC
          </Typography>
          <StyledTable columns={MACHINE_DEFAULT_COLUMNS} rows={currentLineItemData} noDataText="No Data Available" />
        </Paper>

        <Paper className={styles.tableContainer}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              pb: 1,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="h6" sx={{ color: themeMode === DARK_MODE ? '#fff' : '#000' }}>
              Machine Data
            </Typography>
            {serialNumber && <AddDefectButton onClick={() => setIsDefectModalOpen(true)} />}
          </Box>
          <StyledTable
            columns={torqueTableHeader}
            rows={torqueTableRows}
            selectall={true}
            elevated
            paginated={true}
            showCheckBox={true}
            selectedRows={selectedTorqueRows}
            onSelectionChange={handleTorqueSelectionChange}
            noDataText="No Machine Data Available"
          />
        </Paper>
      </Box>

      <Box className={styles.buttonsContainer}>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSkip}
          disabled={selectedTorqueRows.length === 0}
          className={styles.button}
        >
          Fail Forward
        </Button>

        <Button
          variant="contained"
          color="success"
          onClick={isMachineLastOp ? handleMarkComplete : handleNext}
          disabled={isMarkCompleteDisabled()}
          className={styles.button}
        >
          {isMachineLastOp ? 'Mark Complete' : 'Next'}
        </Button>
      </Box>

      <ConfirmMarkCompleteDialog
        open={isConfirmMarkCompleteOpen}
        onClose={() => setIsConfirmMarkCompleteOpen(false)}
        onConfirm={executeMarkComplete}
      />
      <ReLogin
        open={showReLoginModal}
        handleReLogin={handleReLoginSuccess}
        handleClose={() => setShowReLoginModal(false)}
        isDark={themeMode === DARK_MODE}
      />

      {showSkipRemarksModal && (
        <ReasonAndRemarkModal
          nameofModal="Skip Machine Operation"
          open={showSkipRemarksModal}
          handleClose={() => setShowSkipRemarksModal(false)}
          handleSubmit={handleSkipMachineOperation}
          data={{
            vin: currentLineItem?.serialNumber,
            partName: 'Machine Operation',
          }}
          dropDown={[
            'MACHINE ERROR',
            'PARAMETER NOT OK',
            'NO RESPONSE FROM MACHINE',
            'MANUAL SKIP REQUIRED',
            'OTHER',
          ]}
          leftTitle="VIN"
          rightTitle="Operation"
        />
      )}

      <DefectModal
        open={isDefectModalOpen}
        handleClose={() => setIsDefectModalOpen(false)}
        partDetailsRow={[
          {
            serialNumber: serialNumber || '-',
            parentPartNumber: partNumber || '-',
            parentPartDescription: description || '-',
          },
        ]}
        partDetails={{
          serialNumber,
          sequenceNumber: currentLineItem?.sequenceNumber || partValidation?.sequenceNumber,
        }}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  currentLineItem: state.station.currentLineItem,
  allowedOperations: state.operations.allowedOperations,
  themeMode: state.theme.themeMode,
  user: state.user,
  receivePart: state.receivePart,
  partValidation: state.partValidation,
});

const mapDispatchToProps = (dispatch) => ({
  updateOperations: (data) => dispatch(updateOperations(data)),
  updateCurrentLineItem: (data) => dispatch(updateCurrentLineItem(data)),
  markComplete: (success, error, seq) => dispatch(markComplete(success, error, seq)),
  getProcessValidationDetailsForVcNumber: (sn, success, error) =>
    dispatch(getProcessValidationDetailsForVcNumber(sn, success, error)),
  getProcessValidationDetailsForSerialNumber: (sn, success, error) =>
    dispatch(getProcessValidationDetailsForSerialNumber(sn, success, error)),
  privilegedSkip: (asn, payload, success, error) => dispatch(privilegedSkip(asn, payload, success, error)),
  getFeatures: (success, error) => dispatch(getFeatures(success, error)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Machine);